// @typescript-eslint/no-explicit-any
/**
 * The `APUtilSPrintf` class emulates the C sprintf function,
 * formatting a string with specific input arguments.
 */
export class APUtilSPrintf {
  /**
   * Formats a string based on input arguments.
   *
   * @param args An array containing the format string and the values to substitute into it.
   * @returns The formatted string.
   * @throws Will throw an error if minWidth is not finite.
   * @throws Will throw an error if the number of arguments is zero.
   * @throws Will throw an error if there are fewer arguments than specified.
   */
  static sprintf = (...args: unknown[]): string => {
    const regex = /%%|%(?:(\d+)\$)?((?:[-+#0 ]|'[\s\S])*)(\d+)?(?:\.(\d*))?([\s\S])/g;
    let i = 0;
    const format = args[i++];

    const doFormat = function (
      substring: string,
      argIndex: number,
      modifiers: string,
      minWidth: number,
      precision: number | undefined,
      specifier: string
    ) {
      let number, prefix, method, textTransform, value;

      if (substring === '%%') {
        return '%';
      }

      // parse modifiers
      let padChar = ' '; // pad with spaces by default
      let leftJustify = false;
      let positiveNumberPrefix = '';
      let j, l;

      for (j = 0, l = modifiers.length; j < l; j++) {
        switch (modifiers.charAt(j)) {
          case ' ':
          case '0':
            padChar = modifiers.charAt(j);
            break;
          case '+':
            positiveNumberPrefix = '+';
            break;
          case '-':
            leftJustify = true;
            break;
          case "'":
            if (j + 1 < l) {
              padChar = modifiers.charAt(j + 1);
              j++;
            }
            break;
        }
      }

      if (!minWidth) {
        minWidth = 0;
      } else {
        minWidth = +minWidth;
      }

      if (!isFinite(minWidth)) {
        throw new Error('Width must be finite');
      }

      if (!precision) {
        precision = specifier === 'd' ? 0 : 'fFeE'.indexOf(specifier) > -1 ? 6 : undefined;
      } else {
        precision = +precision;
      }

      if (argIndex && +argIndex === 0) {
        throw new Error('Argument number must be greater than zero');
      }

      if (argIndex && +argIndex >= args.length) {
        throw new Error('Too few arguments');
      }

      value = argIndex ? args[+argIndex] : args[i++];

      switch (specifier) {
        case '%':
          return '%';
        case 's':
          return APUtilSPrintf.formatString(value + '', leftJustify, minWidth, precision, padChar);
        case 'S':
          return APUtilSPrintf.formatString(
            value + '',
            leftJustify,
            minWidth,
            precision,
            padChar
          ).toUpperCase();
        case 'c':
          return APUtilSPrintf.formatString(
            String.fromCharCode(+(value as string | number)),
            leftJustify,
            minWidth,
            precision,
            padChar
          );
        case 'b':
          return APUtilSPrintf.formatBaseX(value, 2, leftJustify, minWidth, precision, padChar);
        case 'o':
          return APUtilSPrintf.formatBaseX(value, 8, leftJustify, minWidth, precision, padChar);
        case 'x':
          return APUtilSPrintf.formatBaseX(value, 16, leftJustify, minWidth, precision, padChar);
        case 'X':
          return APUtilSPrintf.formatBaseX(
            value,
            16,
            leftJustify,
            minWidth,
            precision,
            padChar
          ).toUpperCase();
        case 'u':
          return APUtilSPrintf.formatBaseX(value, 10, leftJustify, minWidth, precision, padChar);
        case 'i':
        case 'd':
          number = +(value as string | number) || 0;
          // Plain Math.round doesn't just truncate
          number = Math.round(number - (number % 1));
          prefix = number < 0 ? '-' : positiveNumberPrefix;
          value = prefix + APUtilSPrintf.pad(String(Math.abs(number)), precision || 0, '0', false);

          if (leftJustify && padChar === '0') {
            // can't right-pad 0s on integers
            padChar = ' ';
          }
          return APUtilSPrintf.justify(value, prefix, leftJustify, minWidth, padChar);
        case 'e':
        case 'E':
        case 'f':
        case 'F':
        case 'g':
        case 'G':
          number = +(value as string | number);
          prefix = number < 0 ? '-' : positiveNumberPrefix;
          method = ['toExponential', 'toFixed', 'toPrecision'][
            'efg'.indexOf(specifier.toLowerCase())
          ] as keyof Pick<number, 'toExponential' | 'toFixed' | 'toPrecision'>;
          textTransform = ['toString', 'toUpperCase']['eEfFgG'.indexOf(specifier) % 2];
          value = prefix + Math.abs(number)[method](precision);
          return (
            (APUtilSPrintf.justify(value, prefix, leftJustify, minWidth, padChar) as string)[
              textTransform as keyof string
            ] as () => string
          )();
        default:
          // unknown specifier, consume that char and return empty
          return '';
      }
    };

    try {
      return (format as string).replace(regex, doFormat);
    } catch {
      return '';
    }
  };

  /**
   * Fills the provided string to the specified length.
   *
   * @param str The string to fill.
   * @param len The desired length of the output string.
   * @param chr The character to use for padding.
   * @param leftJustify Whether to pad to the right (false) or to the left (true).
   * @returns The padded string.
   */
  private static pad = (str: string, len: number, chr: string, leftJustify: boolean) => {
    if (!chr) {
      chr = ' ';
    }
    const padding = str.length >= len ? '' : new Array((1 + len - str.length) >>> 0).join(chr);
    return leftJustify ? str + padding : padding + str;
  };

  /**
   * Justifies the given string to the specified minimum length.
   *
   * @param value The string to justify.
   * @param prefix The prefix to add to the string.
   * @param leftJustify Whether to justify to the right (false) or to the left (true).
   * @param minWidth The minimum length of the output string.
   * @param padChar The character to use for padding.
   * @returns The justified string.
   */
  private static justify = (
    value: string,
    prefix: string,
    leftJustify: boolean,
    minWidth: number,
    padChar: string
  ) => {
    const diff = minWidth - value.length;
    if (diff > 0) {
      if (!leftJustify && padChar === '0') {
        value = [
          value.slice(0, prefix.length),
          APUtilSPrintf.pad('', diff, '0', true),
          value.slice(prefix.length),
        ].join('');
      } else {
        value = APUtilSPrintf.pad(value, minWidth, padChar, leftJustify);
      }
    }
    return value;
  };

  /**
   * Formats the string to a specific base (binary, octal, decimal, hexadecimal).
   *
   * @param value The value to format.
   * @param base The base to which the value should be formatted.
   * @param leftJustify Whether to justify to the right (false) or to the left (true).
   * @param minWidth The minimum length of the output string.
   * @param precision The number of digits to use for the formatted value.
   * @param padChar The character to use for padding.
   * @returns The string formatted in the specified base.
   */
  private static formatBaseX = (
    value: unknown,
    base: number,
    leftJustify: boolean,
    minWidth: number,
    precision: number | undefined,
    padChar: string
  ) => {
    // Note: casts negative numbers to positive ones
    const number = (value as number) >>> 0;
    value = APUtilSPrintf.pad(number.toString(base), precision || 0, '0', false);
    return APUtilSPrintf.justify(value as string, '', leftJustify, minWidth, padChar);
  };

  /**
   * Formats the string according to the specified precision and minimum width.
   *
   * @param value The string to format.
   * @param leftJustify Whether to justify to the right (false) or to the left (true).
   * @param minWidth The minimum length of the output string.
   * @param precision The number of characters to use from the input string.
   * @param customPadChar The character to use for padding.
   * @returns The formatted string.
   */
  private static formatString = (
    value: string,
    leftJustify: boolean,
    minWidth: number,
    precision: number | undefined,
    customPadChar: string
  ) => {
    if (precision !== null && precision !== undefined) {
      value = value.slice(0, precision);
    }
    return APUtilSPrintf.justify(value, '', leftJustify, minWidth, customPadChar);
  };
}
